// Multi-select zamestnancu pro polozku operativy.
//
// - Nabidka se plni z ciselniku GET /api/zamestnanci (jen aktivni).
// - Vybrana jmena se zobrazuji jako badge s kriskem; hodnota je pole jmen
//   (polozka je uklada denormalizovane jako text "Jmeno1, Jmeno2").
// - Stara data (volny text mimo ciselnik) zustavaji vybrana a jdou odebrat —
//   jen se u nich nenabizi zadny radek v seznamu.
// - Kdyz hledany text v ciselniku neni, nabidne se inline "Pridat do ciselniku"
//   (POST /api/zamestnanci) — gateovane pravem spravovat ciselniky.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Plus, X } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermissions } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import type { Zamestnanec, ZamestnanecInput } from '@/types/api';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';

const QUERY_KEY = ['zamestnanci'] as const;

interface Props {
  id?: string;
  value: string[];
  onChange: (v: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function ZamestnanciMultiSelect({
  id,
  value,
  onChange,
  disabled,
  placeholder = 'Vyberte zaměstnance…',
}: Props) {
  const { canSpravovatCiselniky } = usePermissions();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const listQuery = useQuery<Zamestnanec[]>({
    queryKey: QUERY_KEY,
    queryFn: () => api<Zamestnanec[]>('/zamestnanci'),
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: (input: ZamestnanecInput) =>
      api<Zamestnanec>('/zamestnanci', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: (novy) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      if (!value.includes(novy.jmeno)) onChange([...value, novy.jmeno]);
      setSearch('');
      toast({ title: `Zaměstnanec „${novy.jmeno}" přidán do číselníku` });
    },
    onError: (err: Error) =>
      toast({ title: 'Přidání selhalo', description: err.message, variant: 'destructive' }),
  });

  // Click-away zavre dropdown.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const zamestnanci = listQuery.data ?? [];
  const hledane = search.trim();

  const options = useMemo(() => {
    const q = hledane.toLocaleLowerCase('cs');
    return zamestnanci
      .filter((z) => z.aktivni)
      .filter((z) => q === '' || z.jmeno.toLocaleLowerCase('cs').includes(q));
  }, [zamestnanci, hledane]);

  // "Pridat" nabizime jen kdyz presne takove jmeno v ciselniku neni (ani neaktivni).
  const existujePresne = zamestnanci.some(
    (z) => z.jmeno.toLocaleLowerCase('cs') === hledane.toLocaleLowerCase('cs'),
  );
  const nabidnoutPridani = canSpravovatCiselniky && hledane !== '' && !existujePresne;

  const toggle = (jmeno: string) => {
    if (value.includes(jmeno)) onChange(value.filter((v) => v !== jmeno));
    else onChange([...value, jmeno]);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Pole s badgy vybranych + hledaci input */}
      <div
        className={cn(
          'flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-sm',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-text',
        )}
        onClick={() => {
          if (disabled) return;
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        {value.map((jmeno) => (
          <Badge key={jmeno} variant="secondary" className="gap-1 font-normal">
            {jmeno}
            {!disabled && (
              <button
                type="button"
                title={`Odebrat ${jmeno}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(value.filter((v) => v !== jmeno));
                }}
                className="rounded-full hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
        <input
          id={id}
          ref={inputRef}
          type="text"
          value={search}
          disabled={disabled}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={value.length === 0 ? placeholder : undefined}
          autoComplete="off"
          className="min-w-[8rem] flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
        />
      </div>

      {open && !disabled && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-60 overflow-y-auto rounded-md border bg-popover p-1 text-sm shadow-md">
          {listQuery.isLoading ? (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {options.map((z) => {
                const vybrano = value.includes(z.jmeno);
                return (
                  <button
                    key={z.id}
                    type="button"
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left hover:bg-accent hover:text-accent-foreground"
                    onClick={() => toggle(z.jmeno)}
                  >
                    <Check className={cn('h-4 w-4 shrink-0', vybrano ? 'opacity-100' : 'opacity-0')} />
                    {z.jmeno}
                  </button>
                );
              })}
              {options.length === 0 && !nabidnoutPridani && (
                <p className="px-2 py-2 text-xs text-muted-foreground">
                  {hledane === ''
                    ? 'Číselník zaměstnanců je prázdný.'
                    : 'Nikdo takový v číselníku není.'}
                </p>
              )}
              {nabidnoutPridani && (
                <button
                  type="button"
                  disabled={createMutation.isPending}
                  className="flex w-full items-center gap-2 rounded-sm border-t px-2 py-1.5 text-left text-primary hover:bg-accent"
                  onClick={() => createMutation.mutate({ jmeno: hledane, aktivni: true })}
                >
                  {createMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Přidat „{hledane}" do číselníku
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
